import React, { useCallback, useState, useEffect, useRef } from "react";
import { toast as alert } from "react-toastify";
import config from "../../../config/config.js";
import tools from "../../../lib/tools.js";
import { getAPIClient } from "../../../services/axios.js";
import business from "../../../services/business.js";
import Dropzone from "../../Form/Dropzone.js";
import InputForm from "../../Form/InputForm.js";
import Loading from "../../Loading.js";
import moment from "moment/moment.js";

const api = getAPIClient();

export default function Anexos({
  className = "mt-60",
  carregarAnexos = true,
  setModel,
  options = {},
  form = {},
  permitirEdicao = false,
}) {
  const [anexos, setAnexos] = useState([]);
  const [uploadStatus, setUploadStatus] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState(false);
  const [loadingAnexos, setLoadingAnexos] = useState(false);
  const [viewingIndex, setViewingIndex] = useState(null);
  const idProcesso = useRef(form.id);

  const onDrop = useCallback(
    (acceptedFiles) => {
      acceptedFiles.map((file) => {
        const extFromName = (file.name.split(".").pop() || "").toLowerCase();
        const extensao = extFromName === "jpeg" ? "jpg" : extFromName;

        anexos.unshift({
          id: null,
          tipo: 1,
          desc_tipo: "",
          identificador: null,
          nome: file.name,
          descricao: "",
          extensao,
          adicionado: false,
          upload: true,
          deleted: false,
          data_cadastro: tools.getDatetime(),
          data_atualizacao: tools.getDatetime(),
          url: null,
          aws: "0",
          file: file,
        });
        setAnexos([...anexos]);
        setModel("anexos", [...anexos]);
        return file;
      });
    },
    [setModel, anexos]
  );

  const uploadFile = (i, anexo) => {
    const formData = new FormData();
    formData.append("file", anexo.file);
    formData.append("id_processo", form.id);
    formData.append("id_tipo", anexo.tipo);
    formData.append("descricao", anexo.descricao);

    setUploadStatus(true);
    anexos[i].upload = true;
    setAnexos([...anexos]);

    api
      .post(`processo/${form.id}/anexos`, formData, {
        headers: { Authorization: `Bearer ${api.getToken()}` },
      })
      .then((response) => {
        if (response.data?.errors && response.data?.errors.length) {
          response.data?.errors.forEach((e) => alert.error(e));
        } else {
          const resp = response.data?.response || {};
          anexos[i].id = resp.id;
          anexos[i].identificador = resp.hash;
          anexos[i].url = null;
          anexos[i].aws = resp.aws ?? anexos[i].aws ?? "0";
          anexos[i].adicionado = resp.status;
          anexos[i].upload = false;
          delete anexos[i].file;
          form.anexos = anexos;
          setAnexos([...anexos]);
          setModel("anexos", [...anexos], true);
        }
      })
      .catch((error) => {
        const status = error?.response?.status;

        const isRequestTooLarge =
          status === 413 || error?.message === "Network Error";

        if (isRequestTooLarge) {
          alert.error(
            "Tamanho de arquivo excedido. Por favor, envie um arquivo menor."
          );
        } else {
          alert.error("Ocorreu um erro ao enviar o arquivo.");
        }
      })
      .finally(() => {
        setUploadStatus(false);
      });
  };

  const viewFile = async (anexo, index) => {
    if (!anexo?.identificador) {
      alert.error("Arquivo ainda não está pronto para visualização.");
      return;
    }

    setViewingIndex(index);

    try {
      const filePath = `/processo/${form.id}/anexos/${anexo.identificador}.${anexo.extensao}`;
      const { data } = await api.get(`${filePath}${anexo.extensao === "zip" ? "/download" : ""}`);

      if (data?.response === false) {
        alert.error("Não foi possível abrir o arquivo.");
      } else {
        window.open(data.response, "_blank");
      }
    } finally {
      setViewingIndex(null);
    }
  };

  const adicionar = async (i, anexo) => {
    if (uploadStatus) return false;
    uploadFile(i, anexo);
  };

  const remover = async (e, i) => {
    e.preventDefault();
    if (deleteStatus) return false;

    if (
      !window.confirm("Deseja realmente deletar o arquivo " + anexos[i].nome + "?")
    ) {
      return false;
    }

    setDeleteStatus(true);
    anexos[i].deleted = true;

    if (anexos[i]?.id) {
      let data = await business.removerAnexo(form.id, anexos[i]);
      if (data?.errors && data?.errors.length) {
        data?.errors.forEach((e) => alert.error(e));
        anexos[i].deleted = false;
        form.anexos = anexos;
        setAnexos(anexos);
      } else if (data?.response) {
        anexos.splice(i, 1);
        form.anexos = anexos;
        setAnexos(anexos);
        setModel("anexos", [...anexos], true);
      }
    } else {
      anexos.splice(i, 1);
      form.anexos = anexos;
      setAnexos(anexos);
      setModel("anexos", [...anexos], true);
    }

    setDeleteStatus(false);
  };

  useEffect(() => {
    async function fetchData() {
      if (!carregarAnexos) return false;

      if (!anexos || !anexos.length) {
        setLoadingAnexos(true);
        try {
          let response = await business.getAnexos(idProcesso.current);
          if (response && response.length) {
            setAnexos(response);
            setModel("anexos", [...response]);
          }
        } finally {
          setLoadingAnexos(false);
        }
      }
    }

    fetchData();
    // eslint-disable-next-line
  }, [carregarAnexos]);

  const handleOrder = async (id, direcao) => {
    //Criar uma cópia do estado para manipular
    const novosAnexos = [...anexos];

    //Encontrar o índice do item a ser movido
    const indexAtual = novosAnexos.findIndex(a => a.id === id);
    if (indexAtual === -1) return;

    // Se a "direção" descer (valor da ordem diminui no banco), no array ele vai para o index anterior.
    const indexVizinho = direcao === "descer" ? indexAtual + 1 : indexAtual - 1;

    // Validar se o vizinho existe dentro dos limites do array
    if (indexVizinho < 0 || indexVizinho >= novosAnexos.length) return;

    //Trocar os itens de lugar no array (Swap)
    const itemAtual = novosAnexos[indexAtual];
    const itemVizinho = novosAnexos[indexVizinho];

    // Trocar 'ordem' apenas para exibição
    const tempOrdem = itemAtual.ordem;
    itemAtual.ordem = itemVizinho.ordem;
    itemVizinho.ordem = tempOrdem;

    novosAnexos[indexAtual] = itemVizinho;
    novosAnexos[indexVizinho] = itemAtual;

    //Atualizar o estado local imediatamente (Optimistic Update)
    setAnexos(novosAnexos);
    setModel("anexos", novosAnexos, true);

    try {
      const payload = { id_anexo: id, direcao: direcao };
      const res = await api.put(`processo/${idProcesso.current}/anexos-reordenar`, payload);

      if (!res.data.response) {
        alert.error("Erro ao sincronizar ordem no servidor.");
      }
    } catch (error) {
      alert.error("Erro na conexão ao reordenar.");
    }
  };

  return (
    <div className={className}>
      <h2 className="f600-18 mb-30 flex jc-sb ai-fc">
        Anexos ({loadingAnexos ? "..." : anexos.length})
      </h2>
      <div>
        {(permitirEdicao ||
          form.id_processo_etapa_status === "3" ||
          form.id_processo_etapa_status === "4" ||
          (config.isEspecialista() && form.id_processo_etapa_status === "6")) && (
            <Dropzone
              onDrop={onDrop}
              description={"Extensões: PDF, PNG, JPG, MP3, OGG e ZIP"}
              accept=".jpeg,.jpg,.png,.pdf,.mp3,.ogg,.zip"
            />
          )}

        <div className="mt-60">
          {loadingAnexos && anexos.length === 0 && (
            <div className="flex jc-fc ai-fc">
              <Loading />
            </div>
          )}

          {(anexos || []).map((anexo, i) => (
            <div key={i} className="anexos flex ai-fc mb-20 gap-20">
              <div className="form-container grid col-12" style={{ gap: '20px 20px' }}>
                <div
                  className="span-8"
                  onClick={() => {
                    viewFile(anexo, i);
                  }}
                >
                  <span className="pointer link flex ai-fc gap-10">
                    <div>
                      {viewingIndex === i ? (
                        <Loading />
                      ) : (
                        <i className="icon icon-add-document i-35 mb-10" />
                      )}
                    </div>
                    <div>
                      <span>{anexo.nome}</span>
                      {anexo.usuario_nome && (
                        <p className="fn-12 pt-5">
                          Adicionado por {anexo.usuario_nome} em:{" "}
                          {moment(anexo.data_cadastro, "YYYY-MM-DD HH:mm").format(
                            "DD/MM/YYYY - HH:mm"
                          )}{" "}
                          hrs
                        </p>
                      )}
                    </div>
                  </span>
                </div>

                <div className="span-4">
                  <InputForm
                    name="tipo"
                    type="select"
                    label="Tipo do documento"
                    exibicao={anexo?.adicionado}
                    value={anexo.tipo}
                    options={options.tiposAnexo}
                    model={(name, value) => {
                      anexos[i][name] = parseInt(value);
                      setAnexos([...anexos]);
                    }}
                  />
                </div>

                <div className="span-9">
                  <InputForm
                    label="Descrição"
                    exibicao={anexo?.adicionado}
                    type="text"
                    value={anexo.descricao}
                    model={(name, value) => {
                      anexos[i].descricao = value;
                      setAnexos([...anexos]);
                    }}
                    name={"anexos." + anexo.id + ".descricao"}
                  />
                </div>

                <div className="span-3">
                  {(((permitirEdicao &&
                    parseInt(form.id_processo_etapa) === 12) ||
                    config.isAdmin() ||
                    config.isEnfermeira()) ||
                    !anexo.id) && (
                      <span
                        className="ml-25 mt-30 pointer float-right"
                        onClick={(e) => remover(e, i)}
                      >
                        {anexo.deleted ? (
                          <Loading />
                        ) : (
                          <i className="icon icon-trash watermelon i-25" title="Remover" />
                        )}
                      </span>
                    )}
                  {anexo.usuario_nome && anexo.ordem ? <span className="ml-15 mt-35 pointer float-right fn-14">Ordem: {anexo.ordem}</span> : ''}
                  {anexo.ordem > 1 && anexos.length > 1 ? <button className="btn-order mt-30 pointer float-right" title="Mover para baixo." onClick={() => handleOrder(anexo.id, 'descer')}>▼</button> : ''}
                  {i > 0 && anexos.length > 1 ? <button className="btn-order mt-30 pointer float-right" title="Mover para cima." onClick={() => handleOrder(anexo.id, 'subir')}>▲</button> : ''}
                  {anexo.upload > 0 && (
                    <button
                      className="btn btn-primary f700-16 mt-20 float-right"
                      disabled={uploadStatus}
                      onClick={() => adicionar(i, anexo)}
                    >
                      {anexo.upload && uploadStatus ? <Loading /> : "Adicionar"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
